import type { LocaleCode } from "@repo/shared/types";
import { createContext, type ReactNode, useContext, useState } from "react";

type UserLanguageContextValue = {
  userLanguage: LocaleCode;
  tenantDefaultLanguage: LocaleCode;
  tenantSupportedLanguages: LocaleCode[];
  setUserLanguage: (lang: LocaleCode) => void;
};

const UserLanguageContext = createContext<UserLanguageContextValue | null>(null);

type UserLanguageProviderProps = {
  userLanguage: string;
  tenantDefaultLanguage: string;
  tenantSupportedLanguages?: string[];
  children: ReactNode;
};

export function UserLanguageProvider({
  userLanguage: initialLanguage,
  tenantDefaultLanguage,
  tenantSupportedLanguages = ["en_GB"],
  children,
}: UserLanguageProviderProps) {
  const [userLanguage, setUserLanguage] = useState<LocaleCode>(initialLanguage as LocaleCode);

  return (
    <UserLanguageContext.Provider
      value={{
        userLanguage,
        tenantDefaultLanguage: tenantDefaultLanguage as LocaleCode,
        tenantSupportedLanguages: tenantSupportedLanguages as LocaleCode[],
        setUserLanguage,
      }}
    >
      {children}
    </UserLanguageContext.Provider>
  );
}

export function useUserLanguage(): UserLanguageContextValue {
  const context = useContext(UserLanguageContext);
  if (!context) {
    // Fallback for components not wrapped in provider
    return {
      userLanguage: "en_GB" as LocaleCode,
      tenantDefaultLanguage: "en_GB" as LocaleCode,
      tenantSupportedLanguages: ["en_GB"] as LocaleCode[],
      setUserLanguage: () => {},
    };
  }
  return context;
}
